<template>
  <article class="chapter-intro">
    <h1 class="chapter-intro-heading">
      {{ heading }}
      <TextToSpeech :text="heading" />
    </h1>

    <div
      v-if="coverPictureId || description"
      class="chapter-intro-section intro-text-wrapper"
      :class="{
        'includes-picture': coverPictureId,
        'includes-video': !coverPictureId && hasVideo,
      }"
    >
      <DynCdnPicture
        class="intro-picture"
        v-if="coverPictureId"
        :width="1000"
        :height="1000"
        :auto-grow="true"
        :picture-id="coverPictureId"
      />
      <div
        v-if="description"
        ref="description"
        class="intro-text"
        v-html="description"
      />
      <VideoPlayer v-if="!coverPictureId && hasVideo" :src="videoUrl" />
    </div>

    <div v-if="coverPictureId && hasVideo" class="chapter-intro-section">
      <VideoPlayer :src="videoUrl" />
    </div>

    <div
      class="chapter-intro-section intro-text-wrapper"
      v-if="chapterData.learningGoals && chapterData.learningGoals.length > 0"
    >
      <LearningGoals :learningGoals="chapterData.learningGoals" />
    </div>

    <ChapterIntroTeacherSection
      v-if="hasTeacherContent"
      :chapter-data="chapterData"
    />
  </article>
</template>

<script>
import ChapterIntroTeacherSection from '@/components/books/ChapterIntroTeacherSection'
import DynCdnPicture from '../media/DynCdnPicture'
import LearningGoals from '@/components/lessons/LearningGoals'
import VideoPlayer from '../media/VideoPlayer'

import textToSpeechMixin from '@/mixins/inject-text-to-speech'
import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { BookStoreNamespacedTypes } from '@/store/books'

import { isOnTeacherSite } from '@/utils/navigation'
import TextToSpeech from '@/components/accessibility/TextToSpeech'

export default {
  name: 'ChapterIntro',
  mixins: [textToSpeechMixin, typesetMathMlMixin],
  components: {
    TextToSpeech,
    ChapterIntroTeacherSection,
    DynCdnPicture,
    LearningGoals,
    VideoPlayer,
  },
  props: {
    chapterData: {
      type: Object,
      required: true,
    },
    chapterIndex: {
      type: Number,
      required: true,
    },
  },
  computed: {
    heading() {
      return this.$t('components.chapter-intro.heading', {
        chapterIndex: this.chapterIndex,
        chapterName: this.chapterData.name,
      })
    },
    description() {
      return this.chapterData.description
    },
    coverPictureId() {
      return this.chapterData.coverPicture.id
    },
    hasTeacherContent() {
      return (
        isOnTeacherSite() &&
        this.$store.getters[
          BookStoreNamespacedTypes.getters.CHAPTER_HAS_TEACHER_CONTENT
        ](this.chapterData.id)
      )
    },
    hasVideo() {
      return !!this.chapterData.video
    },
    videoUrl() {
      return this.hasVideo ? this.chapterData.video.url : null
    },
  },
  methods: {
    updateTextToSpeech() {
      this.injectTextToSpeech(this.$refs.description)
    },
  },
  mounted() {
    this.updateTextToSpeech()
  },
  updated() {
    this.updateTextToSpeech()
  },
}
</script>

<style lang="scss" scoped>
.chapter-intro-heading {
  text-transform: uppercase;

  @media (max-width: $minWidthTablet) {
    text-align: center;
  }
}

::v-deep .chapter-intro-section {
  font-size: $copyFontSize;
  margin: var(--content-holder-margin) 0;
  padding: var(--content-padding-large);
  background: $white;
  border-radius: var(--border-radius);
  overflow: hidden;

  @media (max-width: $minWidthSmallTablet) {
    border-radius: 0;
  }
}

.intro-text-wrapper {
  &.includes-picture {
    display: flex;
    padding: 0;

    .intro-text {
      padding: var(--content-padding-large);
    }
  }

  &.includes-video {
    .video-player {
      margin-top: var(--content-padding-large);
    }
  }
}

.intro-picture,
.intro-text {
  flex-grow: 1;
  flex-basis: 0;
}

.intro-picture {
  &.has-error,
  &.is-letterboxed {
    border-right: 1px solid $grayLighter;
  }
  ::v-deep .image-wrapper {
    background: transparent;
  }
}
</style>
